import React, { useEffect, useState } from "react";
import { Row, Col, Table, Typography, Divider } from "antd";
import { salesRecordWithDetailsApi } from "../../../apiservice/sales";
import { numberFormat, convertNumberToWords, formatToCompanyTimezone } from "../../../lib/functions";
import "./SalesVoucherBodyV2.css";
import { useSelector } from "react-redux";
import { selectAuthInfo } from "../../Auth/authSelectors";
import { getSundryDebitorBalanceApi } from "../../../apiservice/accounts";


const { Title, Text } = Typography;

const SalesInsuranceVoucher = ({ sale_id, printMode = false, hideBonusItems = false, showStamp, showSignature, showShipping }) => {
  const [data, setData] = useState(null);
  const [accDue, setAccDue] = useState(null);

  const { institutionData } = useSelector((state) => state.lookups);

  const authInfo = useSelector(selectAuthInfo);

  const currency = institutionData?.currency;

  const isBonusItem = (item) => Number(item.item_rate) === 0;
  const isIndia = authInfo?.country?.toLowerCase() === "india";
  const taxLabel = isIndia ? "GST" : "VAT";
  const isIndiaGst = authInfo?.gst_enabled;
  const showHSN = isIndia && isIndiaGst;
  const isGSTDisabled =
    isIndia && authInfo?.gst_enabled == "no";

  useEffect(() => {
    if (!sale_id) return;
    salesRecordWithDetailsApi({ sale_id, from: "voucher" }).then((res) => {
      if (res?.data?.length) setData(res.data[0]);
    });
  }, [sale_id]);

  useEffect(() => {
    if (!data) return;
    getSundryDebitorBalanceApi({ customerId: data.acc_id }).then((res) => {
      if (res?.data?.accounts?.length)
        setAccDue(res.data.accounts[0].balance);
    });
  }, [data]);

  if (!data) return null;

  const isIndividual = data.is_cal_type === "individual";
  const items = data.details || [];
  const visibleItems = hideBonusItems
    ? items.filter((item) => !isBonusItem(item))
    : items;
  const hasBonusItems = items.some(isBonusItem);

  // calculate grand total
  const grandTotal = isIndividual
    ? items.reduce((sum, d) => {
      let qty = d.item_qty || d.sale_qty || 0;
      let rate = Number(d.item_rate || 0);
      let total = qty * rate;
      total -= (Number(d.item_discount_per) / 100) * total;
      total += (Number(d.item_tax_per) / 100) * total;
      return sum + total;
    }, 0)
    : data?.total_amount || 0;

  const columns = isIndividual
    ? [
      {
        title: "#", render: (_, __, i) => i + 1, onCell: () => ({
          style: { minWidth: 40 }
        })
      },
      {
        title: "Item Name", dataIndex: "item_name", render: (text, record) => (
          <div>
            <span>
              {text}
              {isBonusItem(record) && (
                <span className="bonus-badge">BONUS</span>
              )}
            </span>

            {record.item_description && (
              <div
                style={{
                  fontSize: 11,
                  color: "#666",
                  marginTop: 2,
                  whiteSpace: "pre-wrap",
                }}
              >
                {record.item_description}
              </div>
            )}
          </div>
        ),
      },
      ...(showHSN
        ? [
          {
            title: "HSN/SAC",
            dataIndex: "hsn_sac_code",
            align: "center",
            width: 100,
          },
        ]
        : []),
      {
        title: "Rate",
        dataIndex: "item_rate",
        align: "right",
        render: (v) => Number(v).toFixed(2),
      },
      {
        title: "Quantity",
        // dataIndex: "item_qty", 
        align: "right",
        render: (_, d) => `${d.item_qty} ${d.unit_symbol}`,
      },
      { title: "Discount%", dataIndex: "item_discount_per", align: "right" },
      ...(!isGSTDisabled
        ? [
          { title: `${taxLabel} `, dataIndex: "item_tax_per", align: "right" },
        ]
        : []),

      {
        title: "Total",
        align: "right",
        render: (_, d) => {
          let qty = d.item_qty || d.sale_qty || 0;
          let rate = Number(d.item_rate || 0);
          let total = qty * rate;
          total -= (Number(d.item_discount_per) / 100) * total;
          total += (Number(d.item_tax_per) / 100) * total;
          return total.toFixed(2);
        },
      },
    ]
    : [
      {
        title: "#", render: (_, __, i) => i + 1, onCell: () => ({
          style: { minWidth: 40 }
        })
      },
      {
        title: "Item",
        dataIndex: "item_name",
        render: (text, record) => (
          <div>
            <span>
              {text}
              {isBonusItem(record) && (
                <span className="bonus-badge">BONUS</span>
              )}
            </span>

            {record.item_description && (
              <div
                style={{
                  fontSize: 11,
                  color: "#666",
                  marginTop: 2,
                  whiteSpace: "pre-wrap",
                }}
              >
                {record.item_description}
              </div>
            )}
          </div>
        ),
      },
      ...(showHSN
        ? [
          {
            title: "HSN/SAC",
            dataIndex: "hsn_sac_code",
            align: "center",
            width: 100,
          },
        ]
        : []),
      {
        title: "Rate",
        dataIndex: "item_rate",
        align: "right",
        render: (v) => Number(v).toFixed(2),
      },
      {
        title: "Quantity",
        // dataIndex: "item_qty", 
        align: "right",
        render: (_, d) => `${d.item_qty} ${d.unit_symbol}`,
      },
      {
        title: "Amount",
        dataIndex: "item_total",
        align: "right",
        render: (v) => Number(v).toFixed(2),
      },
    ];

  return (
    <div className="invoice-v2">
      {/* ================= HEADER ================= */}
      <Row justify="space-between" align="top" className="invoice-header ">
        <Col flex="auto">
          <div className="company-block">

            <div>
              <Title level={4}>{authInfo?.institution_name}</Title>
              <Text className="wrap-text">{authInfo?.address}</Text>
            </div>
          </div>
        </Col>

        <Col flex="320px" className="invoice-title">
          <Title level={2}>INVOICE</Title>
          {/* <Text>Invoice #: {data.sale_voucher_no}</Text><br />
            <Text>
              Date: {formatToCompanyTimezone(data.created_date)?.split(" ")[0]}
            </Text><br />
            <Text>Due Date: {data.due_date || "-"}</Text> */}
        </Col>
      </Row>

      <Divider />

      {/* ================= BILL TO ================= */}
      <Row className="bill-invoice-row" gutter={16}>
        {/* LEFT: Bill To */}
        <Col flex="1 1 60%" className="bill-to">
          <Text strong>Bill To</Text><br />
          <Text>Name: {data.acc_name}</Text><br />
          <Text className="wrap-text">Address: {data.address}</Text><br />
          <Text>Contact No: +{data.contact_no}</Text><br />
          {data?.acc_mgr && <Text className="wrap-text">
            {authInfo?.country?.trim().toLowerCase() === "united arab emirates" &&
              (authInfo?.subscription.toLowerCase().includes("pharma") || authInfo?.subscription.toLowerCase().includes("pharmabooks"))
              ? "Rep. : "
              : "Account Manager : "}{" "}
            {data.acc_mgr && data.acc_mgr}
          </Text>}
          {data.vat_no && <Text>
            {authInfo?.country?.trim().toLowerCase() === "united arab emirates"
              ? "TRN No: "
              : "Tax Reg No: "}{" "}
            {data.vat_no}
          </Text>}
          {showShipping && data.shipping_address && (
            <>
              <br />
              <Text strong>Ship To</Text>
              <br />

              {data.shipping_contact_person && (
                <>
                  <Text>
                    Contact Person : +{data.shipping_contact_person}
                  </Text>
                  <br />
                </>
              )}

              <Text className="wrap-text">
                Address : {data.shipping_address}
              </Text>
              <br />
              {data.shipping_po_box && (
                <>
                  <Text>
                    Po Box : {data.shipping_po_box}
                  </Text>
                  <br />
                </>
              )}

              {data.shipping_contact_no && (
                <>
                  <Text>
                    Contact No : +{data.shipping_contact_no}
                  </Text>
                  <br />
                </>
              )}
            </>
          )}
        </Col>

        {/* RIGHT: Invoice Meta */}
        <Col flex="0 0 280px" className="invoice-meta">
          <div className="invoice-meta-row">
            <span>Invoice No:</span>
            <span>{data.sale_voucher_no}</span>
          </div>

          <div className="invoice-meta-row">
            <span>Date:</span>
            <span>
              {formatToCompanyTimezone(data.created_date)?.split(" ")[0]}
            </span>
          </div>

          <div className="invoice-meta-row">
            <span>Address :</span>
            <span className="wrap-text">{institutionData?.street_address}</span>
          </div>
          {institutionData?.phone_no && <div className="invoice-meta-row">
            <span>Contact No:</span>
            <span>+{institutionData.phone_no || "-"}</span>
          </div>}
          {institutionData?.vat_license && <><div className="invoice-meta-row">
            <span>{institutionData?.country?.trim().toLowerCase() === "united arab emirates"
              ? "TRN No: "
              : "Tax Reg No: "}
            </span>
            <span className="wrap-text">{institutionData?.vat_license}</span>
          </div></>}

          {numberFormat(accDue) > 0 && <div className="invoice-meta-row">
            <span>Due Date:</span>
            <span>{data.due_date || "-"}</span>
          </div>}

          {/* Balance Due UNDER INVOICE */}
          {numberFormat(accDue) > 0 && <div className="invoice-balance-due">
            <span>Total Balance Due</span>
            <strong>{numberFormat(accDue || 0)}</strong>
          </div>}
        </Col>
      </Row>



      {/* Items Table */}
      <Table
        className="invoice-items"
        dataSource={visibleItems}
        columns={columns}
        pagination={false}
        rowKey="item_id"
        size="small"
        rowClassName={(record) =>
          isBonusItem(record) ? "bonus-row" : ""
        }
        summary={() => {
          if (isIndividual) {
            return (
              <Table.Summary.Row>
                <Table.Summary.Cell colSpan={5} align="right">
                  <b>Grand Total</b>
                </Table.Summary.Cell>
                <Table.Summary.Cell align="right">
                  <b>{grandTotal.toFixed(2)}</b>
                </Table.Summary.Cell>
              </Table.Summary.Row>
            );
          }
          return (
            <>
              <Table.Summary.Row>
                <Table.Summary.Cell colSpan={4} align="right">
                  Subtotal
                </Table.Summary.Cell>
                <Table.Summary.Cell align="right">
                  {Number(data.sub_total || 0).toFixed(2)}
                </Table.Summary.Cell>
              </Table.Summary.Row>
              <Table.Summary.Row>
                <Table.Summary.Cell colSpan={4} align="right">
                  Discount
                </Table.Summary.Cell>
                <Table.Summary.Cell align="right">
                  {Number(data.total_discount || 0).toFixed(2)}
                </Table.Summary.Cell>
              </Table.Summary.Row>
              <Table.Summary.Row>
                <Table.Summary.Cell colSpan={4} align="right">
                  VAT
                </Table.Summary.Cell>
                <Table.Summary.Cell align="right">
                  {Number(data.total_tax || 0).toFixed(2)}
                </Table.Summary.Cell>
              </Table.Summary.Row>
              <Table.Summary.Row>
                <Table.Summary.Cell colSpan={4} align="right">
                  <b>Grand Total ({currency})</b>
                </Table.Summary.Cell>
                <Table.Summary.Cell align="right">
                  <b>{Number(grandTotal).toFixed(2)}</b>
                </Table.Summary.Cell>
              </Table.Summary.Row>
            </>
          );
        }}
      />
      {hasBonusItems && <Text style={{ display: "block", fontSize: 11, color: "#666", marginLeft: 10, marginTop: 10 }}>
        *Bonus items are supplied free of cost and not included in taxable value.
      </Text>}

      {/* Footer */}
      <Divider />
      <Row className="bottom-total">
        <Col span={10}>


          <Text>Invoice Due : {numberFormat(data.due_total || 0)}</Text>
          <br />
          {/* <Text>
            Current Customer Due : {numberFormat(data.due_amount || 0)}
          </Text> */}
          <Text>
            Insurance Name : {data.company_name}
          </Text>
          <br />
          <Text>
            Insurance CoPay : {numberFormat(data.copay_amount || 0)} %
          </Text>
          <br />
          <Text>
            Insurance Amt : {numberFormat(data.insurance_amount || 0)}
          </Text>
          <br />
          <Text>
            Customer Amt : {numberFormat(data.patient_amt || 0)}
          </Text>
          <br />
          <Text>
            Doctor : {data.doctor_name}
          </Text>
          <br />


          {/* 🔽 Payment Breakdown */}
          {!printMode && data?.trans && data.trans.length > 0 && (
            <Row gutter={[16, 8]} style={{ marginTop: 12 }}>
              <Col span={10}>
                <Divider orientation="left" plain style={{ margin: "8px 0" }}>
                  Payment Breakdown
                </Divider>
                <Table
                  size="small"
                  bordered
                  pagination={false}
                  style={{ width: "100%" }}  // ✅ take full col width
                  rowKey={(_, index) => index}
                  dataSource={data.trans.map((tran, index) => ({
                    key: index,
                    index: index + 1,
                    account: tran.to_acc_name || "N/A",
                    amount: parseFloat(
                      data?.conversion_rate && data.conversion_rate !== 0
                        ? tran.tran_amount * data.conversion_rate
                        : tran.tran_amount
                    ).toFixed(2),
                  }))}
                  columns={[
                    {
                      title: "#",
                      dataIndex: "index",
                      width: 50,
                    },
                    {
                      title: "Account",
                      dataIndex: "account",
                      // ellipsis: true,
                      onCell: () => ({
                        style: { minWidth: 120 },   // ✅ enforce minWidth
                      }),
                    },
                    {
                      title: "Amount",
                      dataIndex: "amount",
                      align: "right",
                      width: 120,
                      render: (val) => numberFormat(val),
                    },
                  ]}

                />
              </Col>
              <Col span={2}></Col>
            </Row>
          )}
        </Col>


        <Col span={12} style={{ textAlign: "right" }}>
          <div>
            {/* Quantity */}
            <div style={{ display: "flex", textAlign: "left" }}>
              <Text>Quantity Total :</Text>
              <Text>
                ({items.reduce((sum, d) => sum + (Number(d.item_qty) || 0), 0)})
              </Text>
            </div>

            {/* Sub Total */}
            <div style={{ display: "flex", justifyContent: "space-between" }}>
              <Text>Amount Sub Total : </Text>
              <Text>{numberFormat(isIndividual ? grandTotal : data.sub_total || 0)}</Text>
            </div>

            {/* Grand Total */}
            <div
              style={{
                borderTop: "1px solid #000",
                display: "flex",
                justifyContent: "space-between",
                paddingTop: 4,
                marginTop: 4,
              }}
            >
              <Text strong>Grand Total : {currency}</Text>
              <Text strong>{numberFormat(grandTotal)}</Text>
            </div>

            {/* Paid */}
            <div
              style={{
                borderTop: "1px solid #000",
                display: "flex",
                justifyContent: "space-between",
                paddingTop: 4,
                marginTop: 4,
              }}
            >
              <Text strong>Paid : {currency}</Text>
              <Text strong>{numberFormat(data.paid_total || 0)}</Text>
            </div>

            {/* Due */}
            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                paddingTop: 2,
              }}
            >
              <Text strong>Due : {currency}</Text>
              <Text strong>{numberFormat(data.due_total || 0)}</Text>
            </div>
          </div>
        </Col>
      </Row>

      <Divider />
      <Row>
        <Col span={24}>
          <Text>
            In Word of Grand Total :{" "}
            {convertNumberToWords(grandTotal, data?.currency || currency)}
          </Text>
          <br />
          <Text>Description : {data.narration || "-"}</Text>
        </Col>
      </Row>

      {(showStamp || showSignature) && (
        <div className="insurance-invoice-footer-section">
          <div className="footer-right-group">
            {showStamp && institutionData?.stamp && (
              <div className="stamp-block">
                <img src={institutionData.stamp} alt="Stamp" />
              </div>
            )}

            {showSignature && institutionData?.signature && (
              <div className="signature-block">
                <img src={institutionData.signature} alt="Signature" />
                <div className="sign-line-text">Authorized Signature</div>
              </div>
            )}

          </div>
        </div>
      )}
    </div>
  );
};

export default SalesInsuranceVoucher;
